/**
 * Task Management Hooks
 *
 * React Query hooks for interacting with the Task Management API.
 * Provides data fetching, caching, and mutations for tasks.
 *
 * @module hooks/useTasks
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

// ============================================
// Constants
// ============================================

/**
 * Query key factory for task-related queries
 */
export const taskKeys = {
  all: ['tasks'],
  lists: () => [...taskKeys.all, 'list'],
  list: (filters) => [...taskKeys.lists(), filters],
  myTasks: () => [...taskKeys.all, 'my'],
  details: () => [...taskKeys.all, 'detail'],
  detail: (id) => [...taskKeys.details(), id],
  comments: (id) => [...taskKeys.detail(id), 'comments'],
  history: (id) => [...taskKeys.detail(id), 'history']
};

/**
 * API base URL
 */
const API_BASE = '/api/tasks';

/**
 * Extract error message from API response
 * @param {object} errorBody - Parsed error response body
 * @param {string} fallback - Fallback message
 * @returns {string} Error message
 */
function extractErrorMessage(errorBody, fallback) {
  if (!errorBody) return fallback;

  // Handle string error
  if (typeof errorBody.error === 'string') {
    return errorBody.error;
  }

  // Handle Zod validation errors (array of issues)
  if (Array.isArray(errorBody.error)) {
    return errorBody.error.map(e => e.message || String(e)).join(', ');
  }

  // Handle object with message
  if (errorBody.error?.message) {
    return errorBody.error.message;
  }

  // Handle message field directly
  if (errorBody.message) {
    return errorBody.message;
  }

  return fallback;
}

// ============================================
// API Functions
// ============================================

/**
 * Fetch tasks with filters
 * @param {object} filters - Query filters
 * @returns {Promise<object>} Paginated task list
 */
async function fetchTasks(filters = {}) {
  const params = new URLSearchParams();

  if (filters.projectId) params.set('projectId', filters.projectId);
  if (filters.featureId) params.set('featureId', filters.featureId);
  if (filters.status) params.set('status', filters.status);
  if (filters.priority) params.set('priority', filters.priority);
  if (filters.assigneeId) params.set('assigneeId', filters.assigneeId);
  if (filters.assigneeType) params.set('assigneeType', filters.assigneeType);
  if (filters.labelId) params.set('labelId', filters.labelId);
  if (filters.page) params.set('page', String(filters.page));
  if (filters.limit) params.set('limit', String(filters.limit));

  const url = params.toString() ? `${API_BASE}?${params}` : API_BASE;
  console.log('[fetchTasks] Fetching:', url);
  const response = await fetch(url, { credentials: 'include' });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new Error(extractErrorMessage(errorBody, 'Failed to fetch tasks'));
  }

  const data = await response.json();
  console.log('[fetchTasks] Response:', data);
  return data;
}

/**
 * Fetch tasks assigned to current user/agent
 * @param {object} pagination - Pagination options
 * @returns {Promise<object>} Paginated task list
 */
async function fetchMyTasks(pagination = {}) {
  const params = new URLSearchParams();
  if (pagination.page) params.set('page', String(pagination.page));
  if (pagination.limit) params.set('limit', String(pagination.limit));

  const url = params.toString() ? `${API_BASE}/my?${params}` : `${API_BASE}/my`;
  const response = await fetch(url, { credentials: 'include' });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new Error(extractErrorMessage(errorBody, 'Failed to fetch my tasks'));
  }

  return response.json();
}

/**
 * Fetch a single task by ID
 * @param {string} id - Task ID
 * @returns {Promise<object>} Task details
 */
async function fetchTask(id) {
  const response = await fetch(`${API_BASE}/${id}`, { credentials: 'include' });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new Error(extractErrorMessage(errorBody, 'Failed to fetch task'));
  }

  return response.json();
}

/**
 * Create a new task
 * @param {object} data - Task data
 * @returns {Promise<object>} Created task
 */
async function createTask(data) {
  // Debug: Log what we're sending to the API
  console.log('[createTask] Sending data:', JSON.stringify(data, null, 2));

  const response = await fetch(API_BASE, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new Error(extractErrorMessage(errorBody, 'Failed to create task'));
  }

  return response.json();
}

/**
 * Update a task
 * @param {object} params - Update parameters
 * @param {string} params.id - Task ID
 * @param {object} params.data - Updated fields
 * @returns {Promise<object>} Updated task
 */
async function updateTask({ id, data }) {
  const response = await fetch(`${API_BASE}/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new Error(extractErrorMessage(errorBody, 'Failed to update task'));
  }

  return response.json();
}

/**
 * Delete a task (soft delete)
 * @param {string} id - Task ID
 * @returns {Promise<void>}
 */
async function deleteTask(id) {
  const response = await fetch(`${API_BASE}/${id}`, {
    method: 'DELETE',
    credentials: 'include'
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new Error(extractErrorMessage(errorBody, 'Failed to delete task'));
  }
}

/**
 * Update task status
 * @param {object} params - Status update parameters
 * @param {string} params.id - Task ID
 * @param {string} params.status - New status
 * @param {string} [params.reason] - Optional reason for change
 * @returns {Promise<object>} Updated task
 */
async function updateTaskStatus({ id, status, reason }) {
  const response = await fetch(`${API_BASE}/${id}/status`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ status, reason })
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new Error(extractErrorMessage(errorBody, 'Failed to update task status'));
  }

  return response.json();
}

/**
 * Assign task to user/agent
 * @param {object} params - Assignment parameters
 * @param {string} params.id - Task ID
 * @param {string} params.assigneeId - User/Agent ID
 * @param {string} params.assigneeType - 'user' or 'agent'
 * @returns {Promise<object>} Updated task
 */
async function assignTask({ id, assigneeId, assigneeType }) {
  const response = await fetch(`${API_BASE}/${id}/assign`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ assigneeId, assigneeType })
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new Error(extractErrorMessage(errorBody, 'Failed to assign task'));
  }

  return response.json();
}

/**
 * Unassign task
 * @param {string} id - Task ID
 * @returns {Promise<object>} Updated task
 */
async function unassignTask(id) {
  const response = await fetch(`${API_BASE}/${id}/assign`, {
    method: 'DELETE',
    credentials: 'include'
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new Error(extractErrorMessage(errorBody, 'Failed to unassign task'));
  }

  return response.json();
}

/**
 * Update task labels
 * @param {object} params - Label update parameters
 * @param {string} params.id - Task ID
 * @param {string[]} [params.add] - Label IDs to add
 * @param {string[]} [params.remove] - Label IDs to remove
 * @returns {Promise<object>} Updated task
 */
async function updateTaskLabels({ id, add, remove }) {
  const response = await fetch(`${API_BASE}/${id}/labels`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ add, remove })
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new Error(extractErrorMessage(errorBody, 'Failed to update task labels'));
  }

  return response.json();
}

/**
 * Fetch task comments (linked messages)
 * @param {string} id - Task ID
 * @param {object} pagination - Pagination options
 * @returns {Promise<object>} Paginated comments
 */
async function fetchTaskComments(id, pagination = {}) {
  const params = new URLSearchParams();
  if (pagination.page) params.set('page', String(pagination.page));
  if (pagination.limit) params.set('limit', String(pagination.limit));

  const url = params.toString()
    ? `${API_BASE}/${id}/comments?${params}`
    : `${API_BASE}/${id}/comments`;

  const response = await fetch(url, { credentials: 'include' });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new Error(extractErrorMessage(errorBody, 'Failed to fetch task comments'));
  }

  return response.json();
}

/**
 * Fetch task history
 * @param {string} id - Task ID
 * @param {object} pagination - Pagination options
 * @returns {Promise<object>} Paginated history entries
 */
async function fetchTaskHistory(id, pagination = {}) {
  const params = new URLSearchParams();
  if (pagination.page) params.set('page', String(pagination.page));
  if (pagination.limit) params.set('limit', String(pagination.limit));

  const url = params.toString()
    ? `${API_BASE}/${id}/history?${params}`
    : `${API_BASE}/${id}/history`;

  const response = await fetch(url, { credentials: 'include' });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new Error(extractErrorMessage(errorBody, 'Failed to fetch task history'));
  }

  return response.json();
}

// ============================================
// Query Hooks
// ============================================

/**
 * Hook to fetch tasks with filters
 * @param {object} filters - Query filters
 * @param {object} [options] - React Query options
 * @returns {object} Query result
 */
export function useTasks(filters = {}, options = {}) {
  return useQuery({
    queryKey: taskKeys.list(filters),
    queryFn: () => fetchTasks(filters),
    staleTime: 30 * 1000, // 30 seconds
    ...options
  });
}

/**
 * Hook to fetch current user's tasks
 * @param {object} pagination - Pagination options
 * @param {object} [options] - React Query options
 * @returns {object} Query result
 */
export function useMyTasks(pagination = {}, options = {}) {
  return useQuery({
    queryKey: taskKeys.myTasks(),
    queryFn: () => fetchMyTasks(pagination),
    staleTime: 30 * 1000,
    ...options
  });
}

/**
 * Hook to fetch a single task
 * @param {string} id - Task ID
 * @param {object} [options] - React Query options
 * @returns {object} Query result
 */
export function useTask(id, options = {}) {
  return useQuery({
    queryKey: taskKeys.detail(id),
    queryFn: () => fetchTask(id),
    enabled: !!id,
    staleTime: 30 * 1000,
    ...options
  });
}

/**
 * Hook to fetch task comments
 * @param {string} id - Task ID
 * @param {object} pagination - Pagination options
 * @param {object} [options] - React Query options
 * @returns {object} Query result
 */
export function useTaskComments(id, pagination = {}, options = {}) {
  return useQuery({
    queryKey: taskKeys.comments(id),
    queryFn: () => fetchTaskComments(id, pagination),
    enabled: !!id,
    staleTime: 30 * 1000,
    ...options
  });
}

/**
 * Hook to fetch task history
 * @param {string} id - Task ID
 * @param {object} pagination - Pagination options
 * @param {object} [options] - React Query options
 * @returns {object} Query result
 */
export function useTaskHistory(id, pagination = {}, options = {}) {
  return useQuery({
    queryKey: taskKeys.history(id),
    queryFn: () => fetchTaskHistory(id, pagination),
    enabled: !!id,
    staleTime: 60 * 1000, // History changes less often
    ...options
  });
}

// ============================================
// Mutation Hooks
// ============================================

/**
 * Hook to create a new task
 * @returns {object} Mutation result
 */
export function useCreateTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createTask,
    onSuccess: (data) => {
      console.log('[useCreateTask] onSuccess - invalidating queries', data);
      // Invalidate all task-related queries (use 'all' prefix for broader match)
      queryClient.invalidateQueries({ queryKey: taskKeys.all });
      queryClient.invalidateQueries({ queryKey: taskKeys.myTasks() });
    }
  });
}

/**
 * Hook to update a task
 * @returns {object} Mutation result
 */
export function useUpdateTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateTask,
    onSuccess: (data, variables) => {
      // Update the specific task in cache
      queryClient.setQueryData(taskKeys.detail(variables.id), data);
      // Invalidate lists in case sorting/filtering changed
      queryClient.invalidateQueries({ queryKey: taskKeys.lists() });
      queryClient.invalidateQueries({ queryKey: taskKeys.myTasks() });
    }
  });
}

/**
 * Hook to delete a task
 * @returns {object} Mutation result
 */
export function useDeleteTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteTask,
    onSuccess: (_, id) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: taskKeys.detail(id) });
      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: taskKeys.lists() });
      queryClient.invalidateQueries({ queryKey: taskKeys.myTasks() });
    }
  });
}

/**
 * Hook to update task status (with optimistic update)
 * @returns {object} Mutation result
 */
export function useUpdateTaskStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateTaskStatus,
    onMutate: async ({ id, status }) => {
      // Cancel any outgoing refetches to prevent race conditions
      await queryClient.cancelQueries({ queryKey: taskKeys.lists() });
      await queryClient.cancelQueries({ queryKey: taskKeys.myTasks() });
      await queryClient.cancelQueries({ queryKey: taskKeys.detail(id) });

      // Snapshot previous values for rollback
      const previousDetail = queryClient.getQueryData(taskKeys.detail(id));
      const previousLists = queryClient.getQueriesData({ queryKey: taskKeys.lists() });
      const previousMyTasks = queryClient.getQueryData(taskKeys.myTasks());

      // Optimistically update the task detail
      if (previousDetail) {
        queryClient.setQueryData(taskKeys.detail(id), {
          ...previousDetail,
          status
        });
      }

      // Optimistically update all task lists (for Kanban board)
      queryClient.setQueriesData({ queryKey: taskKeys.lists() }, (oldData) => {
        if (!oldData?.data && !oldData?.items) return oldData;
        const tasks = oldData.data || oldData.items || [];
        const updatedTasks = tasks.map(task =>
          task.id === id ? { ...task, status } : task
        );
        return oldData.data
          ? { ...oldData, data: updatedTasks }
          : { ...oldData, items: updatedTasks };
      });

      // Optimistically update my tasks
      queryClient.setQueryData(taskKeys.myTasks(), (oldData) => {
        if (!oldData?.data) return oldData;
        const updatedTasks = oldData.data.map(task =>
          task.id === id ? { ...task, status } : task
        );
        return { ...oldData, data: updatedTasks };
      });

      return { previousDetail, previousLists, previousMyTasks };
    },
    onError: (err, { id }, context) => {
      // Rollback on error
      if (context?.previousDetail) {
        queryClient.setQueryData(taskKeys.detail(id), context.previousDetail);
      }
      if (context?.previousLists) {
        context.previousLists.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      if (context?.previousMyTasks) {
        queryClient.setQueryData(taskKeys.myTasks(), context.previousMyTasks);
      }
    },
    onSettled: (_, __, { id }) => {
      // Always refetch after error or success to ensure consistency
      queryClient.invalidateQueries({ queryKey: taskKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: taskKeys.lists() });
      queryClient.invalidateQueries({ queryKey: taskKeys.myTasks() });
    }
  });
}

/**
 * Hook to assign a task
 * @returns {object} Mutation result
 */
export function useAssignTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: assignTask,
    onSuccess: (data, variables) => {
      queryClient.setQueryData(taskKeys.detail(variables.id), data);
      queryClient.invalidateQueries({ queryKey: taskKeys.lists() });
      queryClient.invalidateQueries({ queryKey: taskKeys.myTasks() });
    }
  });
}

/**
 * Hook to unassign a task
 * @returns {object} Mutation result
 */
export function useUnassignTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: unassignTask,
    onSuccess: (data, id) => {
      queryClient.setQueryData(taskKeys.detail(id), data);
      queryClient.invalidateQueries({ queryKey: taskKeys.lists() });
      queryClient.invalidateQueries({ queryKey: taskKeys.myTasks() });
    }
  });
}

/**
 * Hook to update task labels
 * @returns {object} Mutation result
 */
export function useUpdateTaskLabels() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateTaskLabels,
    onSuccess: (data, variables) => {
      queryClient.setQueryData(taskKeys.detail(variables.id), data);
      queryClient.invalidateQueries({ queryKey: taskKeys.lists() });
    }
  });
}

// ============================================
// Labels and Assignees Hooks
// ============================================

/**
 * Query key factory for labels
 */
export const labelKeys = {
  all: ['labels'],
  project: (projectId) => [...labelKeys.all, 'project', projectId]
};

/**
 * Fetch labels for a project (or global labels if no projectId)
 * @param {string} projectId - Project ID
 * @returns {Promise<Array>} List of labels
 */
async function fetchLabels(projectId) {
  const url = projectId
    ? `/api/labels?projectId=${projectId}`
    : '/api/labels';
  const response = await fetch(url, { credentials: 'include' });

  if (!response.ok) {
    // Return empty array instead of throwing for missing labels
    return [];
  }

  const data = await response.json();
  return data.data || [];
}

/**
 * Hook to fetch labels for a project
 * @param {string} projectId - Project ID
 * @param {object} [options] - React Query options
 * @returns {object} Query result
 */
export function useLabels(projectId, options = {}) {
  return useQuery({
    queryKey: labelKeys.project(projectId),
    queryFn: () => fetchLabels(projectId),
    staleTime: 5 * 60 * 1000, // Labels change rarely
    ...options
  });
}

/**
 * Query key factory for project agents
 */
export const projectAgentKeys = {
  all: ['projectAgents'],
  project: (projectId) => [...projectAgentKeys.all, projectId]
};

/**
 * Fetch all agents (for assignee selection)
 * @returns {Promise<Array>} List of agents
 */
async function fetchAllAgents() {
  const response = await fetch('/api/agents?includeDisabled=false', {
    credentials: 'include'
  });

  if (!response.ok) {
    return [];
  }

  const data = await response.json();
  // Transform to assignee format
  return (data.data || []).map(agent => ({
    id: agent.id,
    name: agent.name || agent.displayName,
    type: 'agent'
  }));
}

/**
 * Hook to fetch agents for task assignment
 * @param {object} [options] - React Query options
 * @returns {object} Query result
 */
export function useProjectAgents(projectId, options = {}) {
  return useQuery({
    queryKey: projectAgentKeys.project(projectId || 'all'),
    queryFn: fetchAllAgents,
    staleTime: 60 * 1000, // 1 minute
    ...options
  });
}

export default {
  taskKeys,
  labelKeys,
  projectAgentKeys,
  useTasks,
  useMyTasks,
  useTask,
  useTaskComments,
  useTaskHistory,
  useCreateTask,
  useUpdateTask,
  useDeleteTask,
  useUpdateTaskStatus,
  useAssignTask,
  useUnassignTask,
  useUpdateTaskLabels,
  useLabels,
  useProjectAgents
};
